import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Chip,
  Autocomplete,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchAllTables } from "../../../../../services/pos/Tables";
import { fetchPendingReservationForTable } from "../../../../../services/pos/reservation/reservationApi";

import TableModalContent from "../../MakeOrder/tables/TableModalContent";
import CreateOrderForm from "../../MakeOrder/tables/OrderDetails/Openorder/CreateOrderForm";
import { changeOrderStatus } from "../../../../../services/OrderStatus/OrderStatusApi";
import { useSettings } from "../../../../../context/SettingsContext";
import { usePermissions } from "../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import OrderItemsDialog from "./OrderItemsDialog";

const statusColors = {
  مفتوح: "#4CAF50",
  متاح: "#03A9F4",
  محجوز: "#F44336",
  "اوردر خارجي": "#fff", // الحالة الجديدة باللون الأبيض
};

const TablesSectionDesktop = () => {
  const { settings } = useSettings();
  // جلب الصلاحيات
  const { permissions } = usePermissions(); // يفترض أن usePermissions يُرجع كائن يحتوي على permissions (مصّفوفة)
  const canOpenTables = permissions["orders_tables_open_table"] === 1;

  const [showNoPermission, setShowNoPermission] = useState(false);

  const { mode } = useContext(ThemeContext);
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [activeTopTab, setActiveTopTab] = useState("all"); // 'all' | 'under_review'
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    captain: "",
  });
  const [selectedTable, setSelectedTable] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectSubOrderModal, setSelectSubOrderModal] = useState(false);
  const [subOrders, setSubOrders] = useState([]);
  const [pendingTable, setPendingTable] = useState(null);
  const [pendingReservations, setPendingReservations] = useState({}); // {tableId: reservationObj}
  const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);

  // حالات Dialog الحجوزات
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogLoading, setReservationDialogLoading] =
    useState(false);
  const [reservationDialogData, setReservationDialogData] = useState([]);
  const [reservationDialogTable, setReservationDialogTable] = useState(null);

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const rootId = localStorage.getItem("branch_id");

  const enrichTables = (data, settings) => {
    // استخدم reservation_time من settings (بالدقائق). إذا لم يوجد، ارجع إلى reservation_alert_before أو 60 كقيمة افتراضية.
    const alertBeforeMinutes = Number(
      settings?.reservation_time ?? settings?.reservation_alert_before ?? 60,
    );

    return data.map((table) => {
      const order = table.order || {};
      let status = table.status || (table.available ? "متاح" : "مفتوح");

      let nearestReservation = null;
      if (Array.isArray(table.reservations) && table.reservations.length > 0) {
        const now = new Date();
        nearestReservation = table.reservations
          .map((r) => {
            const dt = new Date(`${r.reservation_date}T${r.reservation_time}`);
            return { ...r, dt, diffMinutes: Math.round((dt - now) / 60000) };
          })
          .filter((r) => r.diffMinutes >= 0)
          .sort((a, b) => a.diffMinutes - b.diffMinutes)[0];
      }

      if (
        nearestReservation &&
        nearestReservation.diffMinutes >= 0 &&
        nearestReservation.diffMinutes <= alertBeforeMinutes
      ) {
        status = "محجوز";
      }

      // استخراج حالة الأوردر ووقت الفتح مع fallbacks (بما في ذلك احتمالية وجود nested order)
      const orderStatus =
        order.status || order.order?.status || table.order?.status || "";
      const openTime =
        order.open_time ||
        order.openTime ||
        order.order?.open_time ||
        table.order?.open_time ||
        "";

      // تحديد وجود أوردرات فرعية بناءً على الحقول المتاحة داخل order
      const hasSubOrders =
        Boolean(order.has_sub_orders) ||
        Boolean(order.hasSubOrders) ||
        (Array.isArray(order.sub_orders) && order.sub_orders.length > 0) ||
        Boolean(table.hasSubOrders);

      // normalize sub orders array so it's easy للاستخدام لاحقًا
      const subOrders = Array.isArray(order.sub_orders)
        ? order.sub_orders.map((s) => ({
            id: s.id,
            sub_table_name: s.sub_table_name || s.name || `فرعي ${s.id}`,
            open_time: s.open_time || s.openTime || "",
            customer: s.customer || "",
            total: s.total || 0,
            pending_items_count: s.pending_items_count ?? 0,
            // keep raw for any other usage
            ...s,
          }))
        : [];

      return {
        ...table,
        status,
        nearestReservation,
        customer: order.customer || "",
        captain: order.captain_name || "",
        total: order.total || 0,
        orderId: order.id || null,
        // الحقول الجديدة للعرض
        orderStatus,
        openTime,
        // حقل يوضح وجود أوردرات فرعية
        hasSubOrders,
        subOrders,
        // NEW: عدد الأصناف في الأوردر
        orderItemsCount: order.order_items_count ?? 0,
      };
    });
  };

  const loadTables = async () => {
    try {
      setLoading(true);
      const data = await fetchAllTables();
      const enriched = enrichTables(data, settings); // ✅ مرر settings هنا
      setTables(enriched);
      // إذا التاب العلوي مفعل، خزن النتائج المناسبة، وإلا اعرض الكل
      if (activeTopTab === "under_review") {
        setFilteredTables(
          enriched.filter((t) => t.orderStatus === "under_review"),
        );
      } else {
        setFilteredTables(enriched);
      }
    } catch (error) {
      console.error("❌ فشل في تحميل الطاولات:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // جلب بيانات الحجز للطاولات المحجوزة فقط
  const fetchReservationsForReservedTables = async (tablesList) => {
    const reservedTables = tablesList.filter((t) => t.status === "محجوز");
    const reservationsObj = {};
    await Promise.all(
      reservedTables.map(async (table) => {
        try {
          const reservations = await fetchPendingReservationForTable(table.id);
          if (reservations && reservations.length > 0) {
            // احصل على أقرب حجز (حسب الوقت)
            const now = new Date();
            const sorted = reservations
              .map((r) => ({
                ...r,
                diffMinutes: Math.round(
                  (new Date(r.reservation_time) - now) / 60000,
                ),
              }))
              .filter((r) => r.diffMinutes >= 0)
              .sort((a, b) => a.diffMinutes - b.diffMinutes);
            if (sorted.length > 0) {
              reservationsObj[table.id] = sorted[0];
            }
          }
        } catch (e) {
          // تجاهل الخطأ
        }
      }),
    );
    setPendingReservations(reservationsObj);
  };

  // عند تحميل الطاولات أو تحديثها، جلب بيانات الحجز للطاولات المحجوزة (تحديث البيانات كل 1 دقيقة)
  useEffect(() => {
    loadTables();
    const interval = setInterval(() => {
      loadTables();
    }, 9000000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onOrderUpdated = () => {
      loadTables();
    };

    window.addEventListener("orderUpdated", onOrderUpdated);

    return () => {
      window.removeEventListener("orderUpdated", onOrderUpdated);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("current_component", "tables");
  }, []);

  // useEffect(() => {
  //   window.refreshTables = loadTables;
  //   return () => {
  //     delete window.refreshTables;
  //   };
  // }, []);

  window.updateTableData = (tableId) => {
    setTables((prevTables) =>
      prevTables.map((t) =>
        t.id === tableId
          ? {
              ...t,
              status: "مفتوح",
              available: 0,
            }
          : t,
      ),
    );
    setFilteredTables((prevTables) =>
      prevTables.map((t) =>
        t.id === tableId
          ? {
              ...t,
              status: "مفتوح",
              available: 0,
            }
          : t,
      ),
    );
  };

  // عد الطاولات التي في حالة under_review
  const underReviewCount = tables.filter(
    (t) => t.orderStatus === "under_review",
  ).length;

  // عندما يتغير التاب العلوي طبق الفلتر على بيانات الطاولات الحالية
  useEffect(() => {
    if (activeTopTab === "under_review") {
      setFilteredTables(tables.filter((t) => t.orderStatus === "under_review"));
    } else {
      setFilteredTables(tables);
    }
  }, [activeTopTab, tables]);

  const handleFilterChange = (newFilters) => {
    const filtered = tables.filter((table) => {
      return (
        (newFilters.search === "" ||
          table.customer.includes(newFilters.search)) &&
        (newFilters.status === "" || table.status === newFilters.status) &&
        (newFilters.captain === "" || table.captain === newFilters.captain)
      );
    });
    setFilteredTables(filtered);
  };

  const handleTableClick = async (table) => {
    const updatedTable = tables.find((t) => t.id === table.id);

    // تحقق من صلاحية فتح الطاولة
    if (!canOpenTables) {
      // عرض رسالة للمستخدم ومنع فتح المودال
      setShowNoPermission(true);
      return;
    }

    if (!updatedTable) return;

    // إذا توجد أوردرات فرعية، عرض مودال اختيار الأوردر أولاً
    if (updatedTable.hasSubOrders && updatedTable.order) {
      setPendingTable(updatedTable);

      // الأوردر الأساسي (main) من بيانات order نفسها
      const mainOrder = {
        id: updatedTable.orderId || updatedTable.order.id,
        sub_table_name:
          updatedTable.order.sub_table_name ||
          updatedTable.name ||
          `أساسي (${updatedTable.name || updatedTable.orderId || ""})`,
        open_time:
          updatedTable.openTime ||
          updatedTable.order.open_time ||
          updatedTable.order.openTime ||
          "",
        customer:
          updatedTable.order.customer ||
          updatedTable.customer ||
          updatedTable.order.customer_name ||
          "",
        total: updatedTable.order.total || updatedTable.total || 0,
        pending_items_count:
          updatedTable.order.pending_items_count ??
          updatedTable.order.pending_items_count ??
          0,
        isMain: true,
        // ✅ الجديد: حالة الأوردر الأساسي من بيانات الطاولات
        status: updatedTable.orderStatus || updatedTable.order?.status || "",
      };

      const subOrdersList = [
        mainOrder,
        ...(Array.isArray(updatedTable.subOrders)
          ? updatedTable.subOrders
          : []
        ).map((s) => ({
          id: s.id,
          sub_table_name: s.sub_table_name || `فرعي ${s.id}`,
          open_time: s.open_time || "",
          customer: s.customer || "",
          total: s.total || 0,
          pending_items_count: s.pending_items_count ?? 0,
          isMain: false,
        })),
      ];

      setSubOrders(subOrdersList);
      setSelectSubOrderModal(true);
      return;
    }

    setSelectedTable(updatedTable);

    if (!updatedTable.orderId) {
      setModalType("createOrder");
    } else {
      setOrderId(updatedTable.orderId);
      setModalType("addItem");
    }
    setIsModalOpen(true);

    // تحديث البيانات بعد الإجراء
  };

  // const handleSelectSubOrder = (orderObj) => {
  //   setSelectSubOrderModal(false);
  //   if (!pendingTable) return;
  //   setSelectedTable(pendingTable);
  //   setOrderId(orderObj.id);
  //   setModalType("addItem");
  //   setIsModalOpen(true);
  //   setPendingTable(null);
  // };
const handleSelectSubOrder = (orderObj) => {
  // ✅ منع فتح الأوردر المنتهي حتى لو اتعمل call من مكان تاني
  if (isOrderFinished(orderObj.status)) {
    // ممكن تعمل Snackbar بدل alert لو تحب
    alert("هذا الأوردر منتهي ولا يمكن فتحه.");
    return;
  }

  setSelectSubOrderModal(false);
  if (!pendingTable) return;
  setSelectedTable(pendingTable);
  setOrderId(orderObj.id);
  setModalType("addItem");
  setIsModalOpen(true);
  setPendingTable(null);
};

  const refreshAndReopen = async () => {
    // لو عندك selectedTable بالفعل، بس عدل حالته داخليًا بدل ما تعمل fetchAllTables
    if (selectedTable) {
      setSelectedTable((prev) => ({
        ...prev,
        orderId: orderId, // أو أي قيم محدثة عندك
        status: "مفتوح",
      }));

      setModalType("addItem");
      setIsModalOpen(true);
    }
  };

  const handleOrderDeleteSuccess = () => {
    if (!selectedTable) return;

    const updatedTable = {
      ...selectedTable,
      status: "متاح",
      available: 1,
      orderId: null,
      customer: "",
      captain: "",
      total: "0",
      openDateTime: "",
      // مسح الحقول الجديدة
      openTime: "",
      orderStatus: "",
    };

    const newTables = tables.map((t) =>
      t.id === updatedTable.id ? updatedTable : t,
    );

    setTables(newTables);
    setFilteredTables(newTables);
    setSelectedTable(updatedTable);
    setIsModalOpen(false);
    // تحديث البيانات بعد الإجراء
  };

  // فتح Dialog الحجوزات
  const handleShowReservations = async (table) => {
    setReservationDialogOpen(true);
    setReservationDialogTable(table);
    setReservationDialogLoading(true);
    try {
      const data = await fetchPendingReservationForTable(table.id);
      setReservationDialogData(Array.isArray(data) ? data : []);
    } catch {
      setReservationDialogData([]);
    }
    setReservationDialogLoading(false);
  };

  // دالة تأكيد الأوردر الخارجي
  const handleConfirmExternalOrder = async (table) => {
    try {
      if (!table.orderId) {
        alert("لا يوجد رقم أوردر للطاولة!");
        return;
      }
      await changeOrderStatus(table.orderId, "pending");
      setShowConfirmSuccess(true); // إظهار رسالة النجاح
    } catch (err) {
      alert("حدث خطأ أثناء تأكيد الأوردر الخارجي");
      console.error(err);
    }
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  // NEW: حالة الفلتر المتقدم
  const [advancedFilters, setAdvancedFilters] = useState({
    tableNumber: "",
    status: "",
    captain: "",
    customer: "",
  });

  // NEW: دالة تطبيق الفلتر المتقدم
  const applyAdvancedFilters = () => {
    let filtered = tables;

    // فلتر حسب رقم الطاولة
    if (advancedFilters.tableNumber) {
      filtered = filtered.filter((t) =>
        t.name?.toString().includes(advancedFilters.tableNumber),
      );
    }

    // فلتر حسب الحالة
    if (advancedFilters.status) {
      filtered = filtered.filter((t) => t.status === advancedFilters.status);
    }

    // فلتر حسب الكابتن
    if (advancedFilters.captain) {
      filtered = filtered.filter((t) =>
        t.captain
          ?.toLowerCase()
          .includes(advancedFilters.captain.toLowerCase()),
      );
    }

    // فلتر حسب اسم العميل
    if (advancedFilters.customer) {
      filtered = filtered.filter((t) =>
        t.customer
          ?.toLowerCase()
          .includes(advancedFilters.customer.toLowerCase()),
      );
    }

    setFilteredTables(filtered);
  };

  // NEW: دالة إعادة تعيين الفلاتر
  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      tableNumber: "",
      status: "",
      captain: "",
      customer: "",
    });
    setFilteredTables(tables);
  };

  // NEW: تطبيق الفلتر عند تغيير القيم
  useEffect(() => {
    applyAdvancedFilters();
  }, [advancedFilters, tables]);

  // NEW: استخراج قائمة الكباتن الفريدة
  const uniqueCaptains = [
    ...new Set(tables.map((t) => t.captain).filter(Boolean)),
  ];

  // NEW: استخراج قائمة أرقام الطاولات الفريدة
  const uniqueTableNumbers = [
    ...new Set(tables.map((t) => t.name?.toString()).filter(Boolean)),
  ].sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderId(orderId);
    setOrderItemsDialogOpen(true);
  };

  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderId(null);
  };

  // دالة عدم فتح الاوردر المقسم لو تم حفظة
  const isOrderFinished = (status) => {
    const s = (status || "").toLowerCase();
    return s === "finished" || s === "completed" || s === "done";
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* تاب علوي للطلبات قيد المراجعة */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant={activeTopTab === "all" ? "contained" : "outlined"}
          onClick={() => setActiveTopTab("all")}
          size="small"
        >
          كل الطاولات
        </Button>
        <Button
          variant={activeTopTab === "under_review" ? "contained" : "outlined"}
          color="warning"
          onClick={() => setActiveTopTab("under_review")}
          size="small"
        >
          طلب خارجي ({underReviewCount})
        </Button>
      </Box>

      {/* NEW: شريط الفلتر المتقدم */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          bgcolor: mode === "dark" ? "#2C2A3A" : "#f5f5f5",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          🔍 فلتر الطاولات
        </Typography>

        <Grid container spacing={2} alignItems="center">
          {/* CHANGED: فلتر رقم الطاولة - Autocomplete بدلاً من TextField */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              options={uniqueTableNumbers}
              value={advancedFilters.tableNumber}
              onChange={(event, newValue) => {
                setAdvancedFilters((prev) => ({
                  ...prev,
                  tableNumber: newValue || "",
                }));
              }}
              onInputChange={(event, newInputValue) => {
                setAdvancedFilters((prev) => ({
                  ...prev,
                  tableNumber: newInputValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="رقم الطاولة"
                  variant="outlined"
                  size="small"
                  placeholder="ابحث أو اختر رقم الطاولة"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      طاولة {option}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText="لا توجد طاولات مطابقة"
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  paddingRight: "9px !important",
                },
              }}
            />
          </Grid>

          {/* فلتر الحالة */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="الحالة"
              variant="outlined"
              size="small"
              value={advancedFilters.status}
              onChange={(e) =>
                setAdvancedFilters((prev) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
            >
              <MenuItem value="">الكل</MenuItem>
              <MenuItem value="متاح">متاح</MenuItem>
              <MenuItem value="مفتوح">مفتوح</MenuItem>
              <MenuItem value="محجوز">محجوز</MenuItem>
              <MenuItem value="اوردر خارجي">اوردر خارجي</MenuItem>
            </TextField>
          </Grid>

          {/* CHANGED: فلتر الكابتن - Autocomplete بدلاً من Select */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              freeSolo
              options={uniqueCaptains}
              value={advancedFilters.captain}
              onChange={(event, newValue) => {
                setAdvancedFilters((prev) => ({
                  ...prev,
                  captain: newValue || "",
                }));
              }}
              onInputChange={(event, newInputValue) => {
                setAdvancedFilters((prev) => ({
                  ...prev,
                  captain: newInputValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="الكابتن"
                  variant="outlined"
                  size="small"
                  placeholder="ابحث أو اختر الكابتن"
                />
              )}
              noOptionsText="لا يوجد كباتن مطابقين"
            />
          </Grid>

          {/* فلتر العميل */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              label="اسم العميل"
              variant="outlined"
              size="small"
              value={advancedFilters.customer}
              onChange={(e) =>
                setAdvancedFilters((prev) => ({
                  ...prev,
                  customer: e.target.value,
                }))
              }
              placeholder="ابحث باسم العميل"
            />
          </Grid>

          {/* زر إعادة التعيين */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={resetAdvancedFilters}
              size="medium"
              sx={{ height: 40 }}
            >
              إعادة تعيين
            </Button>
          </Grid>
        </Grid>

        {/* عرض الفلاتر النشطة */}
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {advancedFilters.tableNumber && (
            <Chip
              label={`طاولة: ${advancedFilters.tableNumber}`}
              onDelete={() =>
                setAdvancedFilters((prev) => ({ ...prev, tableNumber: "" }))
              }
              color="primary"
              size="small"
            />
          )}
          {advancedFilters.status && (
            <Chip
              label={`حالة: ${advancedFilters.status}`}
              onDelete={() =>
                setAdvancedFilters((prev) => ({ ...prev, status: "" }))
              }
              color="primary"
              size="small"
            />
          )}
          {advancedFilters.captain && (
            <Chip
              label={`كابتن: ${advancedFilters.captain}`}
              onDelete={() =>
                setAdvancedFilters((prev) => ({ ...prev, captain: "" }))
              }
              color="primary"
              size="small"
            />
          )}
          {advancedFilters.customer && (
            <Chip
              label={`عميل: ${advancedFilters.customer}`}
              onDelete={() =>
                setAdvancedFilters((prev) => ({ ...prev, customer: "" }))
              }
              color="primary"
              size="small"
            />
          )}
        </Box>

        {/* عرض عدد النتائج */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: mode === "dark" ? "#aaa" : "#666",
            fontWeight: 500,
          }}
        >
          عدد الطاولات المعروضة: {filteredTables.length} من {tables.length}
        </Typography>
      </Paper>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress color="secondary" size={80} thickness={5} />
        </Box>
      )}

      <Grid container spacing={2}>
        {filteredTables.map((table) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={2.4}
            key={table.id}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <Paper
                sx={{
                  padding: 2,
                  bgcolor: mode === "dark" ? "#2C2A3A" : "#fff",
                  color: mode === "dark" ? "#fff" : "#000",
                  borderRadius: "18px",
                  boxShadow:
                    mode === "dark"
                      ? "0px 4px 12px rgba(0,0,0,0.3)"
                      : "0px 2px 8px rgba(0,0,0,0.1)",
                  position: "relative",
                  cursor: "pointer",
                  minHeight: { xs: "140px", sm: "160px", md: "180px" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                  zIndex: 2,
                  overflow: "hidden",
                }}
                onClick={async () => {
                  await handleTableClick(table);
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "#F8BBD0",
                    color: "#fff",
                    borderRadius: "8px 8px 0 8px",
                    px: 1,
                    py: 0.5,
                    fontWeight: "bold",
                    fontSize: "16px",
                    minWidth: "40px",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    zIndex: 3,
                  }}
                >
                  {/* عرض اسم الطاولة */}
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {`طاولة ${table.name}`}
                    {/* شارة حالة الأوردر تظهر فقط إذا كانت الحالة exactly "under_review" */}
                    {table.orderStatus === "under_review" && (
                      <Box
                        sx={{
                          // using gradient + bright white text for emphasis
                          background:
                            "linear-gradient(90deg, #ff6b6b 0%, #ff8a65 100%)",
                          color: "#ffffff",
                          borderRadius: "10px",
                          px: 1.2,
                          py: "4px",
                          fontWeight: 900,
                          fontSize: "12px",
                          boxShadow:
                            "0 6px 18px rgba(255,107,107,0.32), 0 0 12px rgba(255,138,101,0.22)",
                          border: "1px solid rgba(255,255,255,0.22)",
                          transformOrigin: "center",
                          animation: "pulse 1.6s infinite",
                        }}
                      >
                        {"طلب خارجي"}
                      </Box>
                    )}
                  </Box>

                  {table.hasSubOrders && (
                    <span
                      style={{
                        marginRight: 6,
                        background: "#fff",
                        color: "#d32f2f",
                        borderRadius: "6px",
                        padding: "2px 7px",
                        fontSize: "12px",
                        fontWeight: 700,
                        boxShadow: "0 2px 8px #0002",
                        display: "inline-block",
                        border: "1.5px solid #d32f2f",
                      }}
                      title="هذه الطاولة تحتوي على أوردرات فرعية (مقرره)"
                    >
                      مقرره
                    </span>
                  )}
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "0px",
                    left: 0,
                    bgcolor: statusColors[table.status] || "#999",
                    color: table.status === "اوردر خارجي" ? "#232334" : "#fff",
                    borderRadius: "8px 8px 8px 0",
                    px: 2,
                    py: 0.5,
                    fontWeight: "bold",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "40px",
                    minWidth: "100px",
                  }}
                >
                  {table.status}
                  {table.status === "محجوز" && (
                    <LockIcon sx={{ fontSize: "16px", ml: 1 }} />
                  )}
                </Box>

                {/* CHANGED: أيقونة عدد الأصناف - إضافة onClick */}
                {table.status !== "متاح" && table.orderItemsCount > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 10,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenOrderItems(table.orderId);
                    }}
                  >
                    <Badge
                      badgeContent={table.orderItemsCount}
                      color="error"
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "11px",
                          fontWeight: 700,
                          minWidth: "20px",
                          height: "20px",
                          padding: "0 4px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                          borderRadius: "50%",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                          },
                        }}
                      >
                        <RestaurantMenuIcon
                          sx={{
                            fontSize: 18,
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        />
                      </Box>
                    </Badge>
                  </Box>
                )}

                {/* تنبيه في منتصف بيانات الطاولة إذا كانت "اوردر خارجي" */}
                {table.status === "اوردر خارجي" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "#fff",
                      color: "#d32f2f",
                      borderRadius: "12px",
                      px: 3,
                      py: 1.5,
                      fontWeight: "bold",
                      fontSize: "16px",
                      boxShadow: "0 2px 12px #d32f2f22",
                      border: "2px solid #d32f2f",
                      textAlign: "center",
                      zIndex: 20,
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    تنبيه: يوجد أوردر خارجي قيد المراجعة
                  </Box>
                )}

                {/* تنبيه الحجز للطاولة المحجوزة */}
                {table.status === "محجوز" && pendingReservations[table.id] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 45,
                      left: 20,
                      bgcolor: "#ff9800",
                      color: "#fff",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.5,
                      fontWeight: "bold",
                      fontSize: "13px",
                      zIndex: 5,
                      boxShadow: "0 2px 8px #ff980088",
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    {(() => {
                      const diff = pendingReservations[table.id].diffMinutes;
                      if (diff < 60) {
                        return `الحجز بعد ${diff} دقيقة`;
                      } else {
                        const hours = Math.floor(diff / 60);
                        const mins = diff % 60;
                        return `الحجز بعد ${hours} ساعة${
                          mins > 0 ? ` و${mins} دقيقة` : ""
                        }`;
                      }
                    })()}
                  </Box>
                )}

                {table.status !== "متاح" && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: "75px",
                        left: "8px",
                        color: mode === "dark" ? "#ccc" : "#666",
                        fontSize: "12px",
                      }}
                    >
                      {`فتح: ${formatTime12(table.openTime || table.openDateTime)}`}
                    </Typography>
                    {/* زر عرض الحجوزات - يظهر فقط للطاولات المحجوزة وتحت التاريخ */}
                    {table.status === "محجوز" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 100,
                          left: 8,
                          zIndex: 10,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />} // 👈 صغرت حجم الأيقونة
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowReservations(table);
                          }}
                          sx={{
                            fontSize: "12px", // 👈 صغر الخط
                            padding: "2px 6px", // 👈 قلل المسافات الداخلية
                            minWidth: "unset", // 👈 يمنع البوتون من أخد عرض كبير
                            borderRadius: "6px", // 👈 يخلي الشكل أصغر وأنيق
                          }}
                        >
                          عرض
                        </Button>
                        {/* عد تنازلي للحجز تحت زرار عرض الحجوزات */}
                        {table.nearestReservation && (
                          <Box
                            sx={{
                              mt: 1,
                              fontWeight: "bold",
                              color: "#ff9800",
                              fontSize: "13px",
                              background: "#fffbe6",
                              borderRadius: "8px",
                              px: 2,
                              py: 0.5,
                              boxShadow: "0 2px 8px #ff980088",
                              animation: "blinker 1.2s linear infinite",
                              display: "inline-block",
                            }}
                          >
                            {table.nearestReservation.diffMinutes > 0
                              ? table.nearestReservation.diffMinutes < 60
                                ? `الحجز بعد ${table.nearestReservation.diffMinutes} دقيقة`
                                : (() => {
                                    const hours = Math.floor(
                                      table.nearestReservation.diffMinutes / 60,
                                    );
                                    const mins =
                                      table.nearestReservation.diffMinutes % 60;
                                    return `الحجز بعد ${hours} ساعة${
                                      mins > 0 ? ` و${mins} دقيقة` : ""
                                    }`;
                                  })()
                              : "وقت الحجز الآن"}
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box
                      sx={{ position: "absolute", top: "100px", right: "8px" }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        العميل: {table.customer}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "medium",
                          fontSize: "12px",
                          color: "#aaa",
                        }}
                      >
                        الكابتن: {table.captain}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* تنبيه إذا كان التوتال = 0 */}
                {table.total === 0 && table.status !== "متاح" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 45,
                      left: 20,
                      bgcolor: "#ff1744",
                      color: "#fff",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.5,
                      fontWeight: "bold",
                      fontSize: "13px",
                      zIndex: 5,
                      boxShadow: "0 2px 8px #ff174488",
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    ⚠️
                  </Box>
                )}

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "#F8BBD0",
                    color: "#fff",
                    borderRadius: "8px 0 8px 8px",
                    px: 2,
                    py: 0.5,
                    fontWeight: "bold",
                    fontSize: "14px",
                    minWidth: "70px",
                    textAlign: "center",
                  }}
                >
                  {/* عرض قيمة الأوردر الحقيقي من order.total */}
                  {table.status !== "متاح" ? ` ${table.total}` : null}
                </Box>
                {/* زر تأكيد للأوردر الخارجي */}
                {table.status === "اوردر خارجي" && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      left: 12,
                      zIndex: 10,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="success"
                      sx={{
                        fontWeight: "bold",
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        fontSize: "15px",
                        boxShadow: "0 2px 8px #43a04744",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConfirmExternalOrder(table);
                      }}
                    >
                      تأكيد
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>
        ))}
      </Grid>
      {/* رسالة نجاح تأكيد الأوردر في منتصف الشاشة */}
      <Snackbar
        open={showConfirmSuccess}
        autoHideDuration={3500}
        onClose={() => setShowConfirmSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            bgcolor: "#fffbe6",
            color: "#43a047",
            borderRadius: 2,
            boxShadow: "0 2px 12px #43a04722",
            textAlign: "center",
            px: 3,
            py: 2,
          }}
        >
          ✅ تم تأكيد الأوردر الخارجي بنجاح!
        </Alert>
      </Snackbar>

      {/* Snackbar عند عدم وجود صلاحية لفتح الطاولة */}
      <Snackbar
        open={showNoPermission}
        autoHideDuration={3000}
        onClose={() => setShowNoPermission(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setShowNoPermission(false)}
          sx={{
            fontSize: "15px",
            fontWeight: 700,
            bgcolor: "#ffebee",
            color: "#c62828",
            borderRadius: 1,
          }}
        >
          ليس لديك صلاحية لفتح الطاولة.
        </Alert>
      </Snackbar>

      {/* Dialog عرض الحجوزات */}
      <Dialog
        open={reservationDialogOpen}
        onClose={() => setReservationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          حجوزات الطاولة{" "}
          {reservationDialogTable?.name
            ? `(${reservationDialogTable.name})`
            : ""}
        </DialogTitle>
        <DialogContent>
          {reservationDialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress />
            </Box>
          ) : reservationDialogData.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ my: 2 }}>
              لا توجد حجوزات معلقة لهذه الطاولة.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>الوقت</TableCell>
                    <TableCell>عدد الأشخاص</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>ملاحظات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservationDialogData.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.reservation_date}</TableCell>
                      <TableCell>
                        {
                          // تحويل الوقت إلى نظام 12 ساعة مع AM/PM
                          (() => {
                            const [h, m, s] = r.reservation_time.split(":");
                            let hour = Number(h);
                            const ampm = hour >= 12 ? "م" : "ص";
                            hour = hour % 12 || 12;
                            return `${hour}:${m} ${ampm}`;
                          })()
                        }
                      </TableCell>
                      <TableCell>{r.number_of_people}</TableCell>
                      <TableCell>
                        {r.status === "pending" ? "معلق" : r.status}
                      </TableCell>
                      <TableCell>{r.note || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservationDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

<Dialog
  open={selectSubOrderModal}
  onClose={() => setSelectSubOrderModal(false)}
  maxWidth="xs"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      bgcolor: mode === "dark" ? "#232334" : "#fff",
      color: mode === "dark" ? "#fff" : "#232334",
    },
  }}
>
  <DialogTitle sx={{ fontWeight: 700, fontSize: 19, textAlign: "center" }}>
    اختر الأوردر المطلوب للطاولة
  </DialogTitle>

  <DialogContent>
    <List>
      {subOrders.map((order) => {
        const isFinished = order.status === "finished"; // ✅ هنا الشرط

        return (
          <ListItem key={order.id} disablePadding>
            <ListItemButton
              disabled={isFinished} // ✅ يمنع الفتح
              onClick={() => {
                if (isFinished) return; // ✅ أمان زيادة
                handleSelectSubOrder(order);
              }}
              sx={{
                borderRadius: 2,
                mb: 1,

                // شكل مختلف للأوردر المنتهي
                opacity: isFinished ? 0.55 : 1,
                cursor: isFinished ? "not-allowed" : "pointer",
                border: isFinished ? "1px dashed #999" : (order.isMain ? "2px solid #fbc2eb" : "1px solid #eee"),

                bgcolor: order.isMain ? "#fbc2eb" : undefined,
                color: order.isMain ? "#232334" : undefined,
                fontWeight: order.isMain ? 700 : 500,
              }}
            >
              <ListItemText
                primary={order.sub_table_name}
                secondary={
                  order.isMain
                    ? `الأوردر الأساسي • ${
                        order.status === "pending"
                          ? "معلق"
                          : order.status === "finished"
                          ? "منتهي (لا يمكن فتحه)"
                          : order.status || "-"
                      }`
                    : undefined
                }
                primaryTypographyProps={{
                  fontWeight: order.isMain ? 700 : 500,
                  fontSize: 17,
                }}
                secondaryTypographyProps={{
                  fontSize: 13,
                  color: "#888",
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setSelectSubOrderModal(false)} color="secondary">
      إلغاء
    </Button>
  </DialogActions>
</Dialog>


      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderId}
      />

      <Modal
        open={isModalOpen}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Fade in={isModalOpen}>
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#1F1D2B" : "#fff",
              padding: { xs: 1, sm: 2, md: 3 },
              borderRadius: "16px",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.35)",
              maxHeight:
                modalType === "createOrder"
                  ? { xs: "98vh", sm: "90vh", md: "80vh" }
                  : "95vh",
              height: modalType === "createOrder" ? "auto" : "95vh",
              overflowY: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                width: "6px",
                backgroundColor: "#3a3f4b",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#1a1d22",
                borderRadius: "10px",
              },
              width:
                modalType === "createOrder"
                  ? { xs: "99vw", sm: "90vw", md: "700px" }
                  : { xs: "99vw", sm: "95vw", md: "1200px", lg: "1600px" },
              minWidth:
                modalType === "createOrder"
                  ? { xs: "0", sm: "320px" }
                  : undefined,
              position: "relative",
              maxWidth: "100vw",
            }}
          >
            <IconButton
              onClick={() => setIsModalOpen(false)}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>

            {modalType === "createOrder" && (
              <CreateOrderForm
                tableId={selectedTable?.id}
                rootId={rootId}
                hasReservation={selectedTable?.reservation === 1} // 👈 تمرير حالة الحجز للطاولة
                onSuccess={(newOrder) => {
                  setIsModalOpen(false);

                  // 🔄 تحديث الطاولة الحالية فقط بدون fetchAllTables
                  setTables((prev) =>
                    prev.map((t) =>
                      t.id === selectedTable?.id
                        ? {
                            ...t,
                            orderId: newOrder.id,
                            status: "مفتوح",
                            customer: newOrder.customer || "",
                            captain: newOrder.captain_name || "",
                            total: newOrder.total || 0,
                            openDateTime: new Date().toLocaleString(),
                            // الحقول الجديدة: استخدم القيم من newOrder إن وجدت
                            openTime:
                              newOrder.open_time ||
                              newOrder.openTime ||
                              new Date().toLocaleTimeString() ||
                              "",
                            orderStatus: newOrder.status || "",
                          }
                        : t,
                    ),
                  );

                  setFilteredTables((prev) =>
                    prev.map((t) =>
                      t.id === selectedTable?.id
                        ? {
                            ...t,
                            orderId: newOrder.id,
                            status: "مفتوح",
                            customer: newOrder.customer || "",
                            captain: newOrder.captain_name || "",
                            total: newOrder.total || 0,
                            openDateTime: new Date().toLocaleString(),
                            openTime:
                              newOrder.open_time ||
                              newOrder.openTime ||
                              new Date().toLocaleTimeString() ||
                              "",
                            orderStatus: newOrder.status || "",
                          }
                        : t,
                    ),
                  );

                  setSelectedTable((prev) =>
                    prev
                      ? {
                          ...prev,
                          orderId: newOrder.id,
                          status: "مفتوح",
                          customer: newOrder.customer || "",
                          captain: newOrder.captain_name || "",
                          total: newOrder.total || 0,
                          openDateTime: new Date().toLocaleString(),
                          openTime:
                            newOrder.open_time ||
                            newOrder.openTime ||
                            new Date().toLocaleTimeString() ||
                            "",
                          orderStatus: newOrder.status || "",
                        }
                      : prev,
                  );

                  setOrderId(newOrder.id);
                  setModalType("addItem");
                  setIsModalOpen(true);
                }}
              />
            )}

            {modalType === "addItem" && (
              <TableModalContent
                type="addItem"
                tableId={selectedTable?.id}
                rootId={rootId}
                orderId={orderId}
                onSuccess={refreshAndReopen}
                onClose={() => setIsModalOpen(false)}
                onOrderDeleted={handleOrderDeleteSuccess}
                onOrderChanged={loadTables} // ✅ جديد
              />
            )}
          </Box>
        </Fade>
      </Modal>

      <style>
        {`
          @keyframes blinker {
            50% { opacity: 0.4; }
          }
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 6px 18px rgba(255,107,107,0.12);
            }
            50% {
              transform: scale(1.03);
              box-shadow: 0 10px 30px rgba(255,107,107,0.28);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 6px 18px rgba(255,107,107,0.12);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default TablesSectionDesktop;
